import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'

interface Announcement {
  id: string
  vendor_id: string | null
  announcement_text: string
  expiry_days: number
  created_at: string
  expires_at: string
  image_url: string | null
  /** Null for platform announcements (created by admins). */
  vendors: {
    vendor_name: string
    vendor_logo_url: string | null
  } | null
}

export default function Announcements() {
  const { data: announcements = [], isLoading } = useQuery({
    queryKey: ['announcements'],
    queryFn: async () => {
      try {
        // Fetch active announcements (not expired); left join vendors (null for platform announcements)
        const { data, error } = await supabase
          .from('announcements')
          .select(`
            id,
            vendor_id,
            announcement_text,
            expiry_days,
            created_at,
            expires_at,
            image_url,
            vendors (
              vendor_name,
              vendor_logo_url
            )
          `)
          .gt('expires_at', new Date().toISOString())
          .order('created_at', { ascending: false })

        if (error) {
          console.warn('Error fetching announcements:', error)
          return []
        }

        return (data || []) as Announcement[]
      } catch (err) {
        console.warn('Error in announcements query:', err)
        return []
      }
    },
    retry: false,
    staleTime: 60000, // Cache for 1 minute
  })

  if (isLoading) {
    return (
      <div className="mb-8">
        <p style={{ color: 'var(--text-primary)' }}>Loading announcements...</p>
      </div>
    )
  }

  if (announcements.length === 0) {
    return null // Don't show anything if there are no announcements
  }

  return (
    <div className="mb-8">
      <h2
        className="text-2xl font-bold mb-4"
        style={{ color: 'var(--text-primary)' }}
      >
        Announcements
      </h2>
      <div className="space-y-4">
        {announcements.map((announcement) => (
          <div
            key={announcement.id}
            className="rounded-lg p-4 border-2"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderColor: 'var(--bg-tertiary)',
            }}
          >
            <div className="flex gap-4">
              {/* Announcement image (optional) */}
              {announcement.image_url && (
                <div className="flex-shrink-0">
                  <img
                    src={announcement.image_url}
                    alt=""
                    style={{
                      width: '140px',
                      minHeight: '96px',
                      maxHeight: '180px',
                      objectFit: 'cover',
                      borderRadius: '0.5rem',
                      border: '1px solid var(--bg-tertiary)',
                    }}
                  />
                </div>
              )}

              {/* Vendor Logo (platform announcements have no vendor) */}
              {announcement.vendors?.vendor_logo_url && (
                <div className="flex-shrink-0">
                  <img
                    src={announcement.vendors.vendor_logo_url}
                    alt={`${announcement.vendors.vendor_name} logo`}
                    style={{
                      width: '64px',
                      height: '64px',
                      objectFit: 'contain',
                      borderRadius: '0.5rem',
                    }}
                  />
                </div>
              )}

              {/* Announcement Content */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3
                    className="font-semibold"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {announcement.vendors?.vendor_name ?? 'Platform'}
                  </h3>
                  <span
                    className="text-xs"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {format(new Date(announcement.created_at), 'dd/MM/yyyy')}
                  </span>
                </div>
                <p
                  style={{
                    color: 'var(--text-primary)',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                  }}
                >
                  {announcement.announcement_text}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

